import { ENDPOINTS, EndpointKey } from '@/constants/endpoints';
import { ENV } from '@/constants/env';
import { QueryParams } from '@/hooks/usePagination.types';
import { SearchValueTypeSimple } from '@/hooks/useSortAndFilters.types';

type BuildEndpointOptions = {
    searchParams?: QueryParams;
    additionalPaths?: string[];
};

/**
 * Constructs a URL for the given API endpoint with optional additional paths and search parameters.
 *
 * @param { EndpointKey } endpointKey - The key of the endpoint from the `ENDPOINTS` object.
 * @param { BuildEndpointOptions } params - An object containing the endpoint key, additional paths, and search parameters.
 * @param { Record<string, string> } [params.searchParams] - An optional object containing search parameters as key-value pairs.
 * @param { string[] } [params.additionalPaths] - An optional array of additional path segments to append to the URL.
 * @param { string } [baseUrl] - An optional base URL to use instead of the default REST_API_ENDPOINT value. Useful for testing.
 *
 * @returns {string} The constructed URL.
 *
 * @example
 *   const specificUserOrdersUrl = buildUrl({
 *     endpointKey: 'user',
 *     additionalPaths: ['123', 'orders'],
 *     searchParams: { page: '2', limit: '10' },
 *   });
 *   // Returns: https://abonoteatro-api-dev.ipgestion.net/api/users/123/orders?page=2&limit=10
 */
export const buildEndpointUrl = (
    endpointKey: EndpointKey,
    { additionalPaths, searchParams }: BuildEndpointOptions = {},
    baseUrl: string = ENV.API_URL,
): string => {
    if (!baseUrl) {
        throw new Error('No API endpoint provided. Please set the .env API_URL variable or provide a baseUrl.');
    }

    const fullPath = [baseUrl, ENDPOINTS[endpointKey], ...(additionalPaths ?? [])]
        .filter((path) => !!path) // Skip empty or falsy elements
        .join('/');

    const url = new URL(fullPath);

    if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
            if (typeof value === 'string') {
                url.searchParams.append(key, value);
            } else if (value === null || typeof value === 'number' || typeof value === 'boolean') {
                url.searchParams.append(key, String(value));
            } else {
                value.forEach((val: SearchValueTypeSimple) => url.searchParams.append(key, String(val)));
            }
        });
    }

    return url.toString();
};
